import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Pencil, Briefcase } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { money, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DatePicker } from "@/components/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const NONE = "__none__";
const CURRENT_YEAR = new Date().getFullYear();
const emptyForm = () => ({ name: "", code: "", client_id: NONE, client_job_number: "", status: "active" });

export default function Jobs() {
  const { currentId, current } = useCompany();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);

  const [basis, setBasis] = useState("accrual");
  const [preset, setPreset] = useState("all");
  const [start, setStart] = useState("2015-01-01");
  const [end, setEnd] = useState(todayISO());
  const [report, setReport] = useState(null);

  const applyPreset = (p) => {
    setPreset(p);
    if (p === "all") { setStart("2015-01-01"); setEnd(todayISO()); }
    else if (p === "ty") { setStart(`${CURRENT_YEAR}-01-01`); setEnd(todayISO()); }
    else if (p === "ly") { setStart(`${CURRENT_YEAR - 1}-01-01`); setEnd(`${CURRENT_YEAR - 1}-12-31`); }
  };

  const loadJobs = () => api.get("/jobs").then((r) => setJobs(r.data));
  const loadReport = useCallback(() => {
    if (!currentId) return;
    api.get(`/reports/job-profit?start=${start}&end=${end}&basis=${basis}`).then((r) => setReport(r.data));
  }, [currentId, start, end, basis]);

  useEffect(() => {
    if (!currentId) return;
    loadJobs();
    api.get("/clients").then((r) => setClients(r.data));
  }, [currentId]);

  useEffect(() => { loadReport(); }, [loadReport, jobs]);

  const openNew = () => { setForm(emptyForm()); setEditId(null); setOpen(true); };
  const openEdit = (j) => {
    setForm({ name: j.name, code: j.code || "", client_id: j.client_id || NONE, client_job_number: j.client_job_number || "", status: j.status || "active" });
    setEditId(j.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Job name is required");
    const payload = { name: form.name, code: form.code, status: form.status, client_job_number: form.client_job_number, client_id: form.client_id === NONE ? null : form.client_id };
    try {
      if (editId) await api.put(`/jobs/${editId}`, payload);
      else await api.post("/jobs", payload);
      toast.success(editId ? "Job updated" : "Job created");
      setOpen(false);
      loadJobs();
    } catch (e) {
      toast.error("Could not save job");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this job? Any invoices/expenses coded to it will be uncoded.")) return;
    await api.delete(`/jobs/${id}`);
    loadJobs();
  };

  return (
    <div className="max-w-5xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Jobs &amp; Profitability</h1>
          <p className="text-slate-500 text-sm mt-1">{current?.name} · code invoices &amp; expenses to a job to see what each one earns.</p>
        </div>
        <Button data-testid="add-job-btn" onClick={openNew} className="rounded-md bg-slate-900 hover:bg-slate-800">
          <Plus className="h-4 w-4 mr-2" /> New Job
        </Button>
      </div>

      {/* Profitability report */}
      <Card className="p-4 rounded-lg border-slate-200 shadow-sm mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-end mb-4">
          <div>
            <Label>Period</Label>
            <Select value={preset} onValueChange={applyPreset}>
              <SelectTrigger data-testid="job-preset-select" className="mt-1 rounded-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All dates</SelectItem>
                <SelectItem value="ty">This year ({CURRENT_YEAR})</SelectItem>
                <SelectItem value="ly">Last year ({CURRENT_YEAR - 1})</SelectItem>
                <SelectItem value="custom">Custom range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>From</Label>
            <div className="mt-1"><DatePicker testId="job-report-start" value={start} onChange={(d) => { setStart(d); setPreset("custom"); }} /></div>
          </div>
          <div>
            <Label>To</Label>
            <div className="mt-1"><DatePicker testId="job-report-end" value={end} onChange={(d) => { setEnd(d); setPreset("custom"); }} /></div>
          </div>
          <div>
            <Label>Basis</Label>
            <div className="mt-1 inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="job-basis-toggle">
              {["accrual", "cash"].map((b) => (
                <button key={b} data-testid={`job-basis-${b}`} onClick={() => setBasis(b)}
                  className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${basis === b ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  {b}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="job-profit-report">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-2 font-medium">Job</th>
                <th className="px-4 py-2 font-medium text-right">Income</th>
                <th className="px-4 py-2 font-medium text-right">Expenses</th>
                <th className="px-4 py-2 font-medium text-right">Labour</th>
                <th className="px-4 py-2 font-medium text-right">Profit</th>
              </tr>
            </thead>
            <tbody>
              {(report?.jobs || []).map((r) => (
                <tr key={r.job_id || "unassigned"} className="border-b border-slate-50">
                  <td className="px-4 py-2 text-slate-800">
                    {r.code && <span className="text-slate-400 tabular-nums mr-2">{r.code}</span>}
                    {r.name}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-emerald-700">{money(r.income)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-amber-700">{money(r.expense)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-amber-700">{money(r.labour)}</td>
                  <td className={`px-4 py-2 text-right tabular-nums font-semibold ${r.profit >= 0 ? "text-slate-900" : "text-rose-600"}`}>{money(r.profit)}</td>
                </tr>
              ))}
              {(!report?.jobs || report.jobs.length === 0) && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No income or expenses in this period.</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr className="bg-slate-900 text-white font-heading font-semibold">
                <td className="px-4 py-3">Overall</td>
                <td className="px-4 py-3 text-right tabular-nums" data-testid="job-total-income">{money(report?.total_income)}</td>
                <td className="px-4 py-3 text-right tabular-nums" data-testid="job-total-expense">{money(report?.total_expense)}</td>
                <td className="px-4 py-3 text-right tabular-nums" data-testid="job-total-labour">{money(report?.total_labour)}</td>
                <td className="px-4 py-3 text-right tabular-nums" data-testid="job-net-profit">{money(report?.net_profit)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {/* Jobs list */}
      <Card className="rounded-lg border-slate-200 shadow-sm overflow-hidden p-0">
        {jobs.length === 0 ? (
          <div className="p-16 text-center">
            <Briefcase className="h-8 w-8 mx-auto text-slate-300" />
            <p className="text-slate-400 mt-3 text-sm">No jobs yet. Create one to start coding income &amp; costs to it.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="jobs-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                <th className="px-5 py-3 font-medium">Code</th>
                <th className="px-5 py-3 font-medium">Job</th>
                <th className="px-5 py-3 font-medium">Client</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium text-right"></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" data-testid={`job-row-${j.id}`} onClick={() => navigate(`/jobs/${j.id}`)}>
                  <td className="px-5 py-3 tabular-nums text-slate-500">{j.code || "—"}</td>
                  <td className="px-5 py-3 font-medium text-slate-900">{j.name}</td>
                  <td className="px-5 py-3 text-slate-600">{j.client_name || "—"}</td>
                  <td className="px-5 py-3">
                    <Badge variant="outline" className={`capitalize ${j.status === "closed" ? "bg-slate-100 text-slate-500 border-slate-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                      {j.status}
                    </Badge>
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" data-testid={`edit-job-${j.id}`} onClick={() => openEdit(j)}>
                      <Pencil className="h-4 w-4 text-slate-500" />
                    </Button>
                    <Button variant="ghost" size="icon" data-testid={`delete-job-${j.id}`} onClick={() => remove(j.id)}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Edit Job" : "New Job"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label>Job Name</Label>
                <Input data-testid="job-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-md" placeholder="e.g. Highway 2 As-Builts" />
              </div>
              <div>
                <Label>Code</Label>
                <Input data-testid="job-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1 rounded-md" placeholder="J-001" />
              </div>
            </div>
            <div>
              <Label>Client Job # (their reference)</Label>
              <Input data-testid="job-client-job-number-input" value={form.client_job_number} onChange={(e) => setForm({ ...form, client_job_number: e.target.value })} className="mt-1 rounded-md" placeholder="e.g. ABC-2026-114" />
            </div>
            <div>
              <Label>Client (optional)</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="job-client-select" className="mt-1 rounded-md">
                  <SelectValue placeholder="No client" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  <SelectItem value={NONE}>No client</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="job-status-select" className="mt-1 rounded-md capitalize">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-job-btn" onClick={save} className="rounded-md bg-slate-900 hover:bg-slate-800">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
